/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState, useMemo} from 'react'
import {useIntl, FormattedNumber} from 'react-intl'

// Chakra Components
import {
    Box,
    Stack,
    Grid,
    GridItem,
    Container,
    useDisclosure,
    Skeleton,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

// Project Components
import CartCta from './partials/cart-cta'
import CartTitle from './partials/cart-title'
import ConfirmationModal from '@salesforce/retail-react-app/app/components/confirmation-modal'
import EmptyCart from './partials/empty-cart'
import OrderSummary from '../../components/order-summary'
import StickyCheckoutBar from './partials/sticky-checkout-bar'

import CartEditQuickViewDrawer from '../../components/cart-edit-quick-view-drawer'
import ApproachingDiscounts from '../../components/approaching-discounts'
import BundleProductViewModal from '@salesforce/retail-react-app/app/components/product-view-modal/bundle'
import NoReturnNotification from './partials/cart-no-return-notification'
import LoyaltyCoupon from '../../components/loyalty-coupon'
import ProductItemList from '../../components/product-item-list'
import {useCartCMSContent} from './partials/use-cart-cms-content'
import {useCartVickyInjection} from './partials/use-cart-vicky-injection'
import {renderCarouselItems, renderLayoutsSwitch, renderComponentsSwitch} from '../../components/optimizely/utils/contentRenderer'

// Hooks
import {useCustomToast} from '../../contexts/custom-toast-context'
import { useRemoveGiftCards } from '../../hooks/use-remove-gift-cards';
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import useGeoEstimation from '../../hooks/use-geo-estimation'
import Seo from '../../components/seo'

// Constants
import {API_ERROR_MESSAGE, TOAST_MESSAGE_REMOVED_ITEM_FROM_CART} from '../../constants'

import {REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG} from '@salesforce/retail-react-app/app/pages/cart/partials/cart-secondary-button-group'

// Utilities
import debounce from 'lodash/debounce'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {
    useShopperBasketsMutation,
    useShippingMethodsForShipment
} from '@salesforce/commerce-sdk-react'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {getUpdateBundleChildArray} from '@salesforce/retail-react-app/app/utils/product-utils'
import {sendItemCartEvent, sendViewCartEvent} from '../../utils/gtm-utils'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'

const DEBOUNCE_WAIT = 750

const Cart = () => {
    const styles = useMultiStyleConfig('Cart')
    const {app: appConfig} = getConfig()
    const {site} = useMultiSite()
    const siteId = site?.id
    const {data: basket, refetch, isLoading} = useCurrentBasket()
    const [mounted, setMounted] = useState(false)
    const {data: optimizelyData, isLoading: isOptimizelyLoading} = useOptimizelyPage('cart')
    const {data: cartMiniCartData} = useOptimizelyPage('cart-minicart')

    // Inject VickyAI data into CMS content
    const processedOptimizelyData = useCartVickyInjection(optimizelyData)

    const {banners, carousels} = useCartCMSContent(processedOptimizelyData)

    const cartMiniCartPageData =
        cartMiniCartData?.item || cartMiniCartData?.page || cartMiniCartData
    const shippingGaugeItems = cartMiniCartPageData?.ShippingGaugeText
    const aboveCartItems = cartMiniCartPageData?.AboveCartContent
    const belowProductListItems = cartMiniCartPageData?.BelowProductListArea
    const cmsSignInContent =
        shippingGaugeItems?.length > 0
            ? shippingGaugeItems.map((item, i) => {
                  const block = item?.ContentLink?.Expanded
                  if (!block) return null
                  return (
                      <React.Fragment key={item?.ContentLink?.GuidValue || i}>
                          {renderComponentsSwitch(block)}
                      </React.Fragment>
                  )
              })
            : null

    const {data: customer} = useCurrentCustomer()
    const removeGiftCards = useRemoveGiftCards(basket)

    // Resolve geolocation and submit partial address for estimated taxes/shipping
    useGeoEstimation()
    const [isApplied, setApplied] = useState(false)

    useEffect(() => {
        // Refetch basket to get the c_sflmData
        if (!basket?.c_sflmData) {
            refetch();
        }

        removeGiftCards()
    }, [basket])

    /***************** Product Bundles ************************/
    const bundleChildVariantIds = []
    basket?.productItems?.forEach((productItem) => {
        productItem?.bundledProductItems?.forEach((childProduct) => {
            bundleChildVariantIds.push(childProduct.productId)
        })
    })

    /*****************Basket Mutation************************/
    const updateItemInBasketMutation = useShopperBasketsMutation('updateItemInBasket')
    const updateItemsInBasketMutation = useShopperBasketsMutation('updateItemsInBasket')
    const removeItemFromBasketMutation = useShopperBasketsMutation('removeItemFromBasket')
    const updateShippingMethodForShipmentsMutation = useShopperBasketsMutation(
        'updateShippingMethodForShipment'
    )
    const applyLoyaltyCodeMutation = useShopperBasketsMutation('addCouponToBasket')
    const removeLoyaltyCodeMutation = useShopperBasketsMutation('removeCouponFromBasket')
    /*****************Basket Mutation************************/

    const [selectedItem, setSelectedItem] = useState(undefined)
    const [localQuantity, setLocalQuantity] = useState({})
    const [isCartItemLoading, setCartItemLoading] = useState(false)

    const {isOpen, onOpen, onClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const {showCustomToast} = useCustomToast()
    const modalProps = useDisclosure()

    const cartPageTitle = formatMessage({
        id: 'cart.page.title',
        defaultMessage: 'My Shopping Bag | Garage Clothing UK'
    })
    const cartPageDescription = formatMessage({
        id: 'cart.page.description',
        defaultMessage: 'My Shopping Bag | Garage Clothing UK'
    })

    const [hasFinalSaleInCart, setHasFinalSaleInCart] = useState(false)

    const isShippingDiscountApplied =
        !isLoading &&
        basket?.shippingItems?.some((item) => {
            const freeAdjustments = item.priceAdjustments?.filter(
                (adjustment) => adjustment.appliedDiscount?.type === 'free'
            )
            return freeAdjustments && freeAdjustments.length > 0
        })

    /******************* Shipping Methods for basket shipment *******************/
    // do this action only if the basket shipping method is not defined
    // we need to fetch the shippment methods to get the default value before we can add it to the basket
    useShippingMethodsForShipment(
        {
            parameters: {
                basketId: basket?.basketId,
                shipmentId: 'me'
            }
        },
        {
            // only fetch if basket is has no shipping method in the first shipment
            enabled:
                !!basket?.basketId &&
                basket.shipments.length > 0 &&
                !basket.shipments[0].shippingMethod,
            onSuccess: (data) => {
                updateShippingMethodForShipmentsMutation.mutate({
                    parameters: {
                        basketId: basket?.basketId,
                        shipmentId: 'me'
                    },
                    body: {
                        id: data.defaultShippingMethodId
                    }
                })
            }
        }
    )

    /************************* Error handling ***********************/
    const showError = () => {
        showCustomToast({
            message: formatMessage(API_ERROR_MESSAGE)
        })
    }
    /************************* Error handling ***********************/

    /**************** Wishlist ****************/
    const {data: wishlist, isLoading: isWishListLoading} = useWishList()
    /**************** Wishlist ****************/

    /***************************** Update Cart **************************/
    const handleUpdateCart = async (variant, quantity) => {
        // close the modal before handle the change
        onClose()
        // using try-catch is better than using onError callback since we have many mutation calls logic here
        try {
            setCartItemLoading(true)
            const productIds = basket.productItems.map(({productId}) => productId)

            // The user is selecting different variant, and it has not existed in basket
            if (selectedItem.id !== variant.productId && !productIds.includes(variant.productId)) {
                const item = {
                    productId: variant.productId,
                    quantity,
                    price: variant.price
                }
                return await updateItemInBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    },
                    body: item
                })
            }

            // The user is selecting different variant, and it has existed in basket
            // remove this item in the basket, change the quantity for the new selected variant in the basket
            if (selectedItem.id !== variant.productId && productIds.includes(variant.productId)) {
                await removeItemFromBasketMutation.mutateAsync({
                    parameters: {
                        basketId: basket.basketId,
                        itemId: selectedItem.itemId
                    }
                })
                const basketItem = basket.productItems.find(
                    ({productId}) => productId === variant.productId
                )
                const newQuantity = quantity + basketItem.quantity
                return await changeItemQuantity(newQuantity, basketItem)
            }

            // the user only changes quantity of the same variant
            if (selectedItem.quantity !== quantity) {
                return await changeItemQuantity(quantity, selectedItem)
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    const handleUpdateBundle = async (bundle, bundleQuantity, childProducts) => {
        // close the modal before handle the change
        onClose()

        try {
            setCartItemLoading(true)
            const itemsToBeUpdated = getUpdateBundleChildArray(bundle, childProducts)

            // We only update the parent bundle when the quantity changes
            // Since top level bundles don't have variants
            if (bundle.quantity !== bundleQuantity) {
                itemsToBeUpdated.unshift({
                    itemId: bundle.itemId,
                    productId: bundle.productId,
                    quantity: bundleQuantity
                })
            }

            if (itemsToBeUpdated.length) {
                await updateItemsInBasketMutation.mutateAsync({
                    method: 'PATCH',
                    parameters: {
                        basketId: basket.basketId
                    },
                    body: itemsToBeUpdated
                })
            }
        } catch {
            showError()
        } finally {
            setCartItemLoading(false)
            setSelectedItem(undefined)
        }
    }

    /***************************** Update Cart **************************/

    /***************************** Update quantity **************************/
    const changeItemQuantity = debounce(async (quantity, product) => {
        // This local state allows the dropdown to show the desired quantity
        // while the API call to update it is happening.
        const previousQuantity = localQuantity[product.itemId]
        setLocalQuantity({...localQuantity, [product.itemId]: quantity})
        setCartItemLoading(true)
        setSelectedItem(product)

        await updateItemInBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket?.basketId, itemId: product.itemId},
                body: {
                    productId: product.id,
                    quantity: parseInt(quantity)
                }
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    setLocalQuantity({...localQuantity, [product.itemId]: undefined})
                },
                onError: () => {
                    // reset the quantity to the previous value
                    setLocalQuantity({...localQuantity, [product.itemId]: previousQuantity})
                    showError()
                }
            }
        )
    }, DEBOUNCE_WAIT)

    /***************************** Update quantity **************************/

    /***************************** Remove Item from basket **************************/
    const handleRemoveItem = async (product) => {
        setSelectedItem(product)
        setCartItemLoading(true)
        await removeItemFromBasketMutation.mutateAsync(
            {
                parameters: {basketId: basket.basketId, itemId: product.itemId}
            },
            {
                onSettled: () => {
                    // reset the state
                    setCartItemLoading(false)
                    setSelectedItem(undefined)
                },
                onSuccess: () => {
                    showCustomToast({
                        message: formatMessage(TOAST_MESSAGE_REMOVED_ITEM_FROM_CART, {quantity: 1})
                    })
                    // GTM Modification
                    sendItemCartEvent(product, true)
                },
                onError: () => {
                    showError()
                }
            }
        )
    }

    const [savings, setSavings] = useState(0)
    const [isLoyaltyLoading, setIsLoyaltyLoading] = useState(false)

    // Derive sflmData directly from basket to avoid extra state + effect
    const sflmData = basket?.c_sflmData || null

    // Sync isApplied from basket payment instruments
    useEffect(() => {
        if (basket?.c_sflmData) {
            setApplied(!!basket?.paymentInstruments?.find((pi) => pi.paymentMethodId === 'LOYALTY_POINTS'))
        }
    }, [basket?.c_sflmData, basket?.paymentInstruments])

    // Derive eligibility during render instead of in useEffect (prevents CLS)
    const isEligibleForLoyalty = useMemo(() => {
        if (!sflmData) return false
        return (
            (customer.isRegistered &&
                basket?.productSubTotal > sflmData?.cashDiscountAmount &&
                sflmData?.nonQualifyingPointsBalance >= 150) ||
            isApplied
        ) || false
    }, [sflmData, customer.isRegistered, basket?.productSubTotal, isApplied])

    const seo = <Seo title={cartPageTitle}
            description={cartPageDescription}
            noIndex noFollow
        />

    // GTM Modification
    useEffect(() => {
        if (basket?.productItems) {
            sendViewCartEvent(basket)
        }

        setMounted(true)
    }, [])

    const reedemPoints = async () => {
        if (!sflmData?.couponCode) {
            return
        }

        try {
            setIsLoyaltyLoading(true)
            await applyLoyaltyCodeMutation.mutateAsync(
                {
                    parameters: {basketId: basket?.basketId},
                    body: {
                        code: sflmData?.couponCode
                    }
                },
                {
                    onSuccess: () => {
                        setIsLoyaltyLoading(false)
                        setApplied(true)
                    }
                }
            )
        } catch (e) {
            setIsLoyaltyLoading(false)
            setApplied(false)
            console.error('[reedemPoints] Error when trying to redeem loyalty points', e)
        }
    }

    const removePoints = () => {
        if (!basket?.c_sflmLoyaltyCouponID) {
            return
        }

        setIsLoyaltyLoading(true)

        removeLoyaltyCodeMutation.mutate(
            {
                parameters: {basketId: basket?.basketId, couponItemId: basket?.c_sflmLoyaltyCouponID}
            },
            {
                onSettled: () => {
                    setIsLoyaltyLoading(false)
                    setApplied(false)
                }
            }
        )
    }

    if (!mounted || !basket) {
        return seo
    }

    /********* Rendering  UI **********/
    if (!basket.productItems?.length) {
        return <EmptyCart />
    }

    return (
        <Box {...styles.cartPageRoot} data-testid="sf-cart-container">
            {seo}
            {/* Sticky Checkout Bar */}
            <StickyCheckoutBar basket={basket} />

            <Container {...styles.cartPageContainer} maxW="container.xxxl">
                <Stack {...styles.cartMainStack}>
                    <Stack {...styles.cartContentStack}>
                        {/* Category Banners - Top Area */}
                        {(isOptimizelyLoading || banners.length > 0) && (
                            <Stack
                                {...styles.cartBannerStack}
                                minHeight={isOptimizelyLoading ? {base: '200px', lg: '300px'} : undefined}
                            >
                                <Skeleton isLoaded={!isOptimizelyLoading} height={isOptimizelyLoading ? {base: '200px', lg: '300px'} : 'auto'}>
                                    {banners.map((banner, index) => (
                                        <React.Fragment key={`cart-banner-${index}`}>
                                            {renderLayoutsSwitch(banner)}
                                        </React.Fragment>
                                    ))}
                                </Skeleton>
                            </Stack>
                        )}

                        {aboveCartItems?.length > 0 && (
                            <Box>
                                {aboveCartItems.map((item, i) => {
                                    const block = item?.ContentLink?.Expanded
                                    if (!block) return null
                                    return (
                                        <Box key={item?.ContentLink?.GuidValue || i}>
                                            {renderComponentsSwitch(block)}
                                        </Box>
                                    )
                                })}
                            </Box>
                        )}

                        <Grid {...styles.cartGrid}>
                            <GridItem {...styles.cartMainGridItem}>
                                <CartTitle />

                                <Box {...styles.cartApproachingDiscountBox}>
                                    <ApproachingDiscounts
                                        currency={basket?.currency}
                                        shipments={basket?.shipments}
                                        isApplied={isShippingDiscountApplied}
                                        cmsSignInContent={cmsSignInContent}
                                    />
                                </Box>
                                <Stack {...styles.cartProductListStack}>
                                    <ProductItemList
                                        isOpen={isOpen}
                                        onOpen={onOpen}
                                        onClose={onClose}
                                        setHasFinalSaleInCart={setHasFinalSaleInCart}
                                        showFewItemsLeft={true}
                                    />
                                </Stack>
                                {hasFinalSaleInCart && <NoReturnNotification />}
                                <Box>
                                    {isOpen && selectedItem && !selectedItem.bundledProductItems && (
                                        <CartEditQuickViewDrawer
                                        isOpen={isOpen}
                                        onClose={onClose}
                                        selectedItem={selectedItem}
                                        onUpdateCart={() => {
                                            setCartItemLoading(false)
                                            setSelectedItem(undefined)
                                        }}
                                        />
                                    )}
                                    {isOpen && selectedItem?.bundledProductItems && (
                                        <BundleProductViewModal
                                        isOpen={isOpen}
                                        onOpen={onOpen}
                                        onClose={onClose}
                                        product={selectedItem}
                                        updateCart={(product, quantity, childProducts) =>
                                            handleUpdateBundle(product, quantity, childProducts)
                                        }
                                        />
                                    )}
                                </Box>
                            </GridItem>

                            {/* Right column */}
                            <GridItem {...styles.cartSideGridItem}>
                                <Box {...styles.cartSideGridItemInner}>
                                    <Stack {...styles.cartSideStack}>
                                        {(sflmData || basket?.c_loyaltyV2PointsEarned) &&
                                            basket?.productItems?.some((item) => !item.c_isGiftCard) &&
                                            <LoyaltyCoupon
                                            isPointsApplied={isApplied}
                                            isEligibleForLoyalty={isEligibleForLoyalty}
                                            isLoading={isLoyaltyLoading}
                                            availablePoints={sflmData?.nonQualifyingPointsBalance}
                                            numberOfRedeemedPoints={sflmData?.nonQualifyingPointsToDebit}
                                                cashDiscountAmount={sflmData?.cashDiscountAmount}
                                                currency={basket?.currency}
                                                pointsEarned={basket?.c_loyaltyV2PointsEarned}
                                                reedemPoints={reedemPoints}
                                                removePoints={removePoints}
                                            />
                                        }
                                        <OrderSummary
                                            showPromoCodeForm={true}
                                            showPostalCodeForm={true}
                                            isEstimate={true}
                                            basket={basket}
                                            savings={savings}
                                            page={'cart'}
                                            />
                                        <Box>
                                            <CartCta isMiniCart={false} />
                                        </Box>
                                    </Stack>
                                </Box>
                            </GridItem>
                        </Grid>

                        {belowProductListItems?.length > 0 && (
                            <Box>
                                {belowProductListItems.map((item, i) => {
                                    const block = item?.ContentLink?.Expanded
                                    if (!block) return null
                                    return (
                                        <Box key={item?.ContentLink?.GuidValue || i}>
                                            {renderComponentsSwitch(block)}
                                        </Box>
                                    )
                                })}
                            </Box>
                        )}

                        {/* VickyAI Carousels - Bottom Area */}
                        {getSiteEnvVarValue(appConfig, 'VICKY_ENABLED', siteId) === 'true' && (
                            <Box>
                                {isOptimizelyLoading && (
                                    <Box>
                                        <Skeleton height="30px" width="300px" mb={4} />
                                        <Box {...styles.cartCarouselLoadingWrapper}>
                                            {[0, 1, 2, 3, 4].map((key) => (
                                                <Box key={key} {...styles.cartCarouselLoadingCard}>
                                                    <Stack {...styles.cartCarouselLoadingCardStack}>
                                                        <Skeleton height="250px" />
                                                        <Skeleton height="20px" width="80%" />
                                                        <Skeleton height="20px" width="60%" />
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>
                                )}
                                {!isOptimizelyLoading && carousels.length > 0 && (
                                    <Stack {...styles.cartCarouselStack}>
                                        {carousels.map((carousel, index) => (
                                            <Box key={`cart-carousel-${index}`}>
                                                {renderCarouselItems(carousel, `cart-carousel-${index}`)}
                                            </Box>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        )}
                    </Stack>
                </Stack>
            </Container>
            <ConfirmationModal
                {...REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG}
                onPrimaryAction={() => {
                    handleRemoveItem(selectedItem)
                }}
                onAlternateAction={() => {}}
                {...modalProps}
            />
        </Box>
    )
}

Cart.getTemplateName = () => 'cart'

export default Cart
